import React, { useMemo } from 'react';
import { Card } from '@/designSystem/Card';
import {
  CircleDollarSign,
  TrendingUp,
  Megaphone,
  Eye,
  ClipboardCheck,
  Target,
  BarChart3,
  LineChart,
  Lightbulb,
  ChevronRight,
  Zap
} from 'lucide-react';
import { derivePropertyMetrics, generateFreeRecommendations } from '../utils/generators';
import { RealtimeCompetitor, RealtimeProperty } from '@/features/realtimeProperty/types';
import { Button } from '../../../designSystem/Button';

interface FreeRecommendationsProps {
  property: RealtimeProperty;
  competitors: RealtimeCompetitor[];
  currencySymbol: string;
  onConsultationClick?: () => void;
  showLeadGenElements?: boolean;
}

export const FreeRecommendations: React.FC<FreeRecommendationsProps> = ({
  property,
  competitors,
  currencySymbol,
  onConsultationClick,
  showLeadGenElements,
}) => {
  // Derive metrics from the available data
  const propertyMetrics = derivePropertyMetrics(property, competitors);

  // Generate real insights using the metrics
  const realInsights = generateFreeRecommendations(propertyMetrics);

  // Sort real insights by impact level (high to low) and take top 6
  const topInsights = useMemo(() => {
    return [...realInsights]
      .sort((a, b) => {
        const impactOrder = { high: 3, medium: 2, low: 1 };
        return impactOrder[b.impactLevel.toLowerCase() as 'high' | 'medium' | 'low'] -
          impactOrder[a.impactLevel.toLowerCase() as 'high' | 'medium' | 'low'];
      })
      .slice(0, 6);
  }, [realInsights]);

  // Calculate potential revenue based on insights and property data
  const potentialRevenue = useMemo(() => {
    const baseAdr = property.data_points.length > 0
      ? property.data_points[property.data_points.length - 1].price.our || 100
      : 100;

    let totalPotential = 0;
    realInsights.forEach(insight => {
      const multiplier =
        insight.impactLevel === 'high' ? 30 :
          insight.impactLevel === 'medium' ? 15 : 5;

      const categoryFactor =
        insight.category.toLowerCase() === 'pricing' ? 1.5 :
          insight.category.toLowerCase() === 'visibility' ? 1.2 :
            insight.category.toLowerCase() === 'marketing' ? 1.0 : 0.8;

      totalPotential += baseAdr * multiplier * categoryFactor;
    });

    return Math.round(totalPotential);
  }, [realInsights, property]);

  // If no insights were generated, don't render anything
  if (!realInsights.length) {
    return null;
  }

  const getCategoryIcon = (category: string) => {
    switch (category.toLowerCase()) {
      case 'pricing':
        return CircleDollarSign;
      case 'revenue':
        return TrendingUp;
      case 'marketing':
        return Megaphone;
      case 'visibility':
        return Eye;
      case 'policies':
        return ClipboardCheck;
      case 'strategy':
        return Target;
      case 'conversion':
        return BarChart3;
      case 'occupancy':
        return LineChart;
      case 'performance':
        return TrendingUp;
      default:
        return Lightbulb;
    }
  };

  // Get color based on impact level
  const getImpactColor = (level: string) => {
    switch (level.toLowerCase()) {
      case 'high':
        return 'bg-red-50 text-red-700 border-red-200';
      case 'medium':
        return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case 'low':
        return 'bg-green-50 text-green-700 border-green-200';
      default:
        return 'bg-blue-50 text-blue-700 border-blue-200';
    }
  };

  return (
    <Card className="overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-purple-50 border-b">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Zap className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Key Recommendations</h3>
            <p className="text-sm text-gray-600">Actionable insights to boost your performance</p>
          </div>
        </div>
        {showLeadGenElements && (
          <div className="text-right">
            <div className="text-sm font-medium text-green-700">
              Potential {currencySymbol}: +<span className='blur-sm'>{currencySymbol}{potentialRevenue.toLocaleString()}</span>
            </div>
            <div className="text-xs text-gray-500">per month</div>
          </div>
        )}
      </div>

      <div className="p-4">
        {/* Recommendations Grid - Optimized for 1/3 width layout */}
        <div className="space-y-2 mb-4">
          {topInsights.slice(0, 4).map((insight, index) => {
            const IconComponent = getCategoryIcon(insight.category);
            return (
              <div
                key={`insight-${index}`}
                className={`group p-2 border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-sm transition-all bg-white ${showLeadGenElements ? 'cursor-pointer' : ''}`}
                onClick={onConsultationClick}
              >
                <div className="flex items-start gap-2">
                  <div className="mt-0.5">
                    <IconComponent className="w-3 h-3 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-md font-medium text-gray-900 leading-tight mb-1">
                      {insight.message}
                    </p>
                    <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500 capitalize">
                        {insight.category}
                      </span>
                      <div className={`text-xs capitalize py-1 px-2 rounded-full font-medium ${getImpactColor(insight.impactLevel)}`}>
                        {insight.impactLevel} Impact
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Premium CTA - More compact */}
        {showLeadGenElements && (
          <div className="flex gap-2 items-center justify-between p-3 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200">
            <div className="flex items-center gap-3">
              <div className="p-1.5 bg-blue-100 rounded">
                <Target className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-blue-900">
                  More premium strategies available
                </p>
                <p className="text-xs text-blue-700">
                  Get implementation guides & detailed action plans
                </p>
              </div>
            </div>
            <Button
              onClick={onConsultationClick}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
            >
              Unlock All
              <ChevronRight className="w-3 h-3" />
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
};